import {
	createFileRoute,
	Link,
	Navigate,
	Outlet,
	useLocation,
	useMatches,
	useNavigate,
	useRouteContext,
} from '@tanstack/react-router';
import React, { Suspense, useState } from 'react';
import { Image } from '../-components/image';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { useNgoProfileQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { isPublicNgoRoute, resolveNgoRouteAccess } from '@/lib/ngo-route-access';
import {
	BookOpenIcon,
	ChartIcon,
	DocumentTextIcon,
	LoginIcon,
	MessageQuestionIcon,
	NoteIcon,
	SettingIcon,
} from './-components/assets/sidebar-icons';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	useSidebar,
} from '@worldcoo/ui/components/sidebar';
import { DraftBanner } from './-components/draft-banner';
import { FaqPopup } from './-components/faq-popup';
import { MainSection } from './-components/main-section';
import { useQueryClient, useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { navigateWithViewTransition } from '@/lib/view-transition-utils';
import { openUsercentricsPrivacySettings } from '@worldcoo/ui/lib/usercentrics';
import { clearPersistedQueryCache } from '@/lib/query-persistence';
import { FormTranslationsProvider } from '../-components/form-translations-provider';

export const Route = createFileRoute('/ngos')({
	// Never awaited, so the SSR stream is not blocked.
	loader: ({ context }) => {
		context.queryClient.prefetchQuery(trpc.auth.getNgoSession.queryOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	// Request-scoped: concurrent SSR requests clobber the module global into React #418 (TEC-3481).
	const { i18n } = useRouteContext({ from: '__root__' });
	const matches = useMatches();
	const routeId = matches[matches.length - 1]?.routeId ?? '';

	if (isPublicNgoRoute(routeId)) {
		return (
			<I18nProvider i18n={i18n}>
				<FormTranslationsProvider>
					<Outlet />
				</FormTranslationsProvider>
			</I18nProvider>
		);
	}

	return (
		<I18nProvider i18n={i18n}>
			<FormTranslationsProvider>
				<NgoAuthenticatedLayout isFaqPage={routeId === '/ngos/faq'} />
			</FormTranslationsProvider>
		</I18nProvider>
	);
}

/**
 * The NGO area's one access guard (lib/ngo-route-access), redirecting before any `ngo.*` query mounts.
 * No <Suspense fallback>: the session is a synchronous cookie read, and a fallback would blank the page.
 * It RENDERS <Navigate>: a redirect thrown during render kills the SSR boundary (React #419, TEC-3440).
 */
function NgoAuthenticatedLayout({ isFaqPage }: { isFaqPage: boolean }) {
	const { data: signedIn } = useSuspenseQuery(trpc.auth.getNgoSession.queryOptions());
	const matches = useMatches();
	const routeId = matches[matches.length - 1]?.routeId ?? '';
	const { pathname } = useLocation();
	// A later pass already reports `/login`, which would become the post-login target.
	const [initialPathname] = useState(pathname);

	const redirect = resolveNgoRouteAccess({ routeId, signedIn, pathname: initialPathname });
	if (redirect) return <Navigate to={redirect.to} search={redirect.search} replace />;

	return <NgoLayout isFaqPage={isFaqPage} />;
}

function NgoLayout({ isFaqPage }: { isFaqPage: boolean }) {
	return (
		<SidebarProvider
			defaultOpen
			className="h-screen bg-[#fff7ed]"
			style={{ '--primary': '#f28e41', '--sidebar-width': '192px' } as React.CSSProperties}
		>
			<NgoSidebar />
			<MainSection>
				<DraftBanner />
				<Outlet />
			</MainSection>

			{!isFaqPage && <FaqPopup />}
		</SidebarProvider>
	);
}

function NgoSidebar() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { isMobile, setOpenMobile } = useSidebar();
	const { mutate: logout } = useMutation(
		trpc.auth.logout.mutationOptions({
			onSuccess: () => {
				clearPersistedQueryCache(queryClient);
				navigateWithViewTransition(
					() => navigate({ to: '/ngos/login' }),
					() => !document.querySelector('[data-slot="sidebar"]'),
				);
			},
		}),
	);

	const closeMobileOnNav = () => {
		if (isMobile) setOpenMobile(false);
	};

	return (
		<Sidebar
			collapsible="offcanvas"
			className="bg-transparent group-data-[side=left]:border-r-0 [&_[data-slot=sidebar-inner]]:bg-transparent"
			style={{ viewTransitionName: 'ngo-sidebar' }}
		>
			<SidebarHeader className="px-3 py-4">
				<Suspense fallback={<SidebarLogoSkeleton />}>
					<SidebarLogo />
				</Suspense>
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu className="gap-1">
							<NgoMenuItem label={t`My results`} icon={ChartIcon} to="/ngos/dashboard" onNavigate={closeMobileOnNav} />
							<NgoMenuItem
								label={t`Projects`}
								icon={DocumentTextIcon}
								to="/ngos/projects"
								onNavigate={closeMobileOnNav}
							/>
							<NgoMenuItem label={t`Worldcoo Plus`} icon={NoteIcon} to="/ngos/portals" onNavigate={closeMobileOnNav} />
							<NgoMenuItem label={t`Toolkit`} icon={BookOpenIcon} to="/ngos/resources" onNavigate={closeMobileOnNav} />
							<NgoMenuItem label={t`Settings`} icon={SettingIcon} to="/ngos/settings" onNavigate={closeMobileOnNav} />
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter className="px-2 pb-4">
				<SidebarMenu className="gap-1">
					<NgoMenuItem label={t`FAQs`} icon={MessageQuestionIcon} to="/ngos/faq" onNavigate={closeMobileOnNav} />
					{/* The consent control, here because the NGO area has a sidebar instead of a page footer. */}
					<SidebarMenuItem>
						<SidebarMenuButton variant="portal" size="portal" asChild>
							<button
								type="button"
								onClick={() => {
									closeMobileOnNav();
									openUsercentricsPrivacySettings();
								}}
								className="cursor-pointer"
							>
								<NgoMenuIcon icon={SettingIcon} />
								<span>{t`Privacy Settings`}</span>
							</button>
						</SidebarMenuButton>
					</SidebarMenuItem>
					<SidebarMenuItem>
						<SidebarMenuButton variant="portal" size="portal" asChild>
							<button
								type="button"
								onClick={() => {
									closeMobileOnNav();
									logout();
								}}
								className="cursor-pointer"
							>
								<NgoMenuIcon icon={LoginIcon} />
								<span>{t`Log Out`}</span>
							</button>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>
		</Sidebar>
	);
}

function NgoMenuItem(props: {
	label: string;
	icon: React.ComponentType<React.SVGAttributes<SVGSVGElement>>;
	to: '/ngos/dashboard' | '/ngos/projects' | '/ngos/portals' | '/ngos/resources' | '/ngos/faq' | '/ngos/settings';
	onNavigate?: () => void;
}) {
	return (
		<SidebarMenuItem>
			<SidebarMenuButton variant="portal" size="portal" asChild>
				<Link to={props.to} onClick={props.onNavigate}>
					<NgoMenuIcon icon={props.icon} />
					<span>{props.label}</span>
				</Link>
			</SidebarMenuButton>
		</SidebarMenuItem>
	);
}

function NgoMenuIcon(props: { icon: React.ComponentType<React.SVGAttributes<SVGSVGElement>> }) {
	const Icon = props.icon;
	return (
		<div className="flex size-8 shrink-0 items-center justify-center rounded-lg p-1 transition-colors group-data-[status=active]/menu-button:bg-[#f28e41]">
			<Icon className="!size-5 text-[#1d1d1b] transition-colors group-data-[status=active]/menu-button:text-white" />
		</div>
	);
}

function SidebarLogo() {
	const ngo = useNgoProfileQuery();

	return (
		<div className="flex items-center gap-2">
			{ngo.logo && typeof ngo.logo === 'string' ? (
				<div className="border-portal-border flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white">
					<Image
						src={ngo.logo}
						alt={ngo.name ?? ''}
						height={24}
						aspectRatio={1}
						objectFit="contain"
						className="size-6 object-contain"
					/>
				</div>
			) : (
				<div className="border-portal-border flex size-8 shrink-0 items-center justify-center rounded-lg border bg-white text-xs font-bold text-[#f28e41]">
					{ngo.name?.charAt(0)?.toUpperCase() ?? 'N'}
				</div>
			)}
			<span className="truncate text-base font-medium text-[#1d1d1b]">{ngo.name ?? 'NGO'}</span>
		</div>
	);
}

function SidebarLogoSkeleton() {
	return (
		<div className="flex items-center gap-2">
			<Skeleton className="size-8 rounded-lg" />
			<Skeleton className="h-5 w-16" />
		</div>
	);
}
